import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Circle, Group, Rect, Text } from 'react-konva'
import type Konva from 'konva'
import type { KonvaEventObject } from 'konva/lib/Node'
import { FloorTable } from '../../types/floorPlan.model'
import { useFloorPlanStore } from '../../store/useFloorPlanStore'
import { MIN_TABLE_SIZE, snapToGrid } from '../../utils/defaults'
import { getTableFootprint, rotatedFootprintAABB } from '../../utils/seatLayout'
import SeatRing from '../SeatRing/SeatRing'
import {
  orangeShades,
  primaryColors
} from '@/assets/styles/helpers/colors.styles'

const TABLE_FILL = primaryColors.orange
const TABLE_STROKE = orangeShades.shade2
const TABLE_STROKE_SELECTED = orangeShades.shade2
const TABLE_HALO = orangeShades.shade1
const LABEL_COLOR = primaryColors.white
const SEATS_LABEL_COLOR = primaryColors.white

interface TableNodeProps {
  table: FloorTable
  isSelected: boolean
  isMultiSelected: boolean
  /** False under the hand tool, the table can't be selected or dragged. */
  interactive: boolean
  onSelect: (additive: boolean) => void
  onChange: (
    patch: Partial<
      Pick<FloorTable, 'x' | 'y' | 'width' | 'height' | 'rotation'>
    >
  ) => void
  registerNode: (id: string, node: Konva.Group | null) => void
  /** True when a footprint box centred at (x, y) overlaps another table. */
  collidesWith: (
    id: string,
    centerX: number,
    centerY: number,
    footprintWidth: number,
    footprintHeight: number
  ) => boolean
  /** Where the group may move this table to while several are dragged. */
  boundGroupDrag: (center: { x: number; y: number }) => {
    x: number
    y: number
  }
  onGroupDragStart: () => void
  onGroupDragMove: () => void
  onGroupDragEnd: () => void
}

function TableNode(props: TableNodeProps) {
  const {
    table,
    isSelected,
    isMultiSelected,
    interactive,
    onSelect,
    onChange,
    registerNode,
    collidesWith,
    boundGroupDrag,
    onGroupDragStart,
    onGroupDragMove,
    onGroupDragEnd
  } = props
  const groupRef = useRef<Konva.Group | null>(null)

  const { t } = useTranslation()
  const snapEnabled = useFloorPlanStore(s => s.snapEnabled)
  const { shape, width, height, label, seatCount } = table
  const seatsLabel = t('FLOOR_PLAN.SEAT_COUNT', { count: seatCount })
  const stroke = isSelected ? TABLE_STROKE_SELECTED : TABLE_STROKE
  const strokeWidth = isSelected ? 2.5 : 1.5

  const handleDragEnd = (e: KonvaEventObject<DragEvent>) => {
    // Group origin sits at the table center, so convert back to top-left.
    onChange({
      x: e.target.x() - width / 2,
      y: e.target.y() - height / 2
    })
  }

  const dragBoundFunc = (pos: { x: number; y: number }) => {
    const stage = groupRef.current?.getStage()
    const scale = stage?.scaleX() || 1
    const panX = stage?.x() ?? 0
    const panY = stage?.y() ?? 0
    // Konva passes absolute (screen) coords; convert back to model space
    // (account for pan + zoom). Placement is fully free (no bounds), the
    // planner can be panned and the "centre on tables" button finds anything.
    const modelX = (pos.x - panX) / scale
    const modelY = (pos.y - panY) / scale
    const center = {
      x: snapEnabled ? snapToGrid(modelX) : modelX,
      y: snapEnabled ? snapToGrid(modelY) : modelY
    }

    // During a group drag the whole selection moves rigidly, so the canvas
    // checks collision for the group as one (`boundGroupDrag`) rather than
    // each table on its own, which would tear the group apart.
    if (isMultiSelected) {
      const bounded = boundGroupDrag(center)
      return { x: bounded.x * scale + panX, y: bounded.y * scale + panY }
    }

    // Collision: block movement into another table (chairs included), but allow
    // sliding along an axis that stays clear so the table glides along its
    // neighbour's edge. The footprint accounts for the table's rotation.
    const fp = rotatedFootprintAABB(
      getTableFootprint(shape, seatCount, width, height),
      table.rotation
    )
    let cx = center.x
    let cy = center.y
    if (collidesWith(table.id, cx, cy, fp.width, fp.height)) {
      const node = groupRef.current
      const curCx = node ? node.x() : table.x + width / 2
      const curCy = node ? node.y() : table.y + height / 2
      if (!collidesWith(table.id, cx, curCy, fp.width, fp.height)) {
        cy = curCy
      } else if (!collidesWith(table.id, curCx, cy, fp.width, fp.height)) {
        cx = curCx
      } else {
        cx = curCx
        cy = curCy
      }
    }
    return { x: cx * scale + panX, y: cy * scale + panY }
  }

  const handleTransformEnd = () => {
    const node = groupRef.current
    if (!node) return

    const scaleX = node.scaleX()
    const scaleY = node.scaleY()

    // Konva applies scale during transform; bake it into real dimensions and
    // reset scale so seats recompute from actual width/height (not scale).
    const newWidth = Math.max(MIN_TABLE_SIZE, width * scaleX)
    const newHeight = Math.max(MIN_TABLE_SIZE, height * scaleY)

    node.scaleX(1)
    node.scaleY(1)

    const center = {
      x: snapEnabled ? snapToGrid(node.x()) : node.x(),
      y: snapEnabled ? snapToGrid(node.y()) : node.y()
    }
    const newRotation = node.rotation()
    node.position(center)

    onChange({
      x: center.x - newWidth / 2,
      y: center.y - newHeight / 2,
      width: newWidth,
      height: newHeight,
      rotation: newRotation
    })
  }

  return (
    <Group
      ref={node => {
        groupRef.current = node
        registerNode(table.id, node)
      }}
      x={table.x + width / 2}
      y={table.y + height / 2}
      rotation={table.rotation}
      draggable={interactive}
      dragBoundFunc={dragBoundFunc}
      onClick={e => interactive && onSelect(e.evt.shiftKey)}
      onTap={() => interactive && onSelect(false)}
      onDragStart={() => {
        if (isMultiSelected) onGroupDragStart()
        else onSelect(false)
      }}
      onDragMove={() => {
        if (isMultiSelected) onGroupDragMove()
      }}
      onDragEnd={e => {
        if (isMultiSelected) onGroupDragEnd()
        else handleDragEnd(e)
      }}
      onTransformEnd={handleTransformEnd}
    >
      {shape === 'round' ? (
        <Circle
          radius={width / 2}
          fill={TABLE_FILL}
          stroke={stroke}
          strokeWidth={strokeWidth}
          shadowColor={TABLE_HALO}
          shadowBlur={8}
          shadowOpacity={0.3}
        />
      ) : (
        <Rect
          x={-width / 2}
          y={-height / 2}
          width={width}
          height={height}
          cornerRadius={shape === 'square' ? 8 : 10}
          fill={TABLE_FILL}
          stroke={stroke}
          strokeWidth={strokeWidth}
          shadowColor={TABLE_HALO}
          shadowBlur={8}
          shadowOpacity={0.3}
        />
      )}

      <SeatRing
        shape={shape}
        seatCount={seatCount}
        width={width}
        height={height}
      />

      {/* Counter-rotate the labels around the table center so they stay
          upright/readable no matter how the table itself is rotated. */}
      <Group rotation={-table.rotation}>
        <Text
          text={label}
          fontSize={16}
          fontStyle="bold"
          fill={LABEL_COLOR}
          width={width}
          x={-width / 2}
          y={-16}
          align="center"
          listening={false}
        />
        <Text
          text={seatsLabel}
          fontSize={13}
          fill={SEATS_LABEL_COLOR}
          width={width}
          x={-width / 2}
          y={2}
          align="center"
          listening={false}
        />
      </Group>
    </Group>
  )
}

export default TableNode
